;
import { useDateTimeFormat, DateTimeFormatOptions } from '@/hooks/useDateTimeFormat';
import { cn } from '@/lib/utils';

interface DateTimeProps {
    /** UTC date string from database */
    date: string;
    /** Display format */
    format?: 'date' | 'time' | 'datetime' | 'relative' | 'custom';
    /** Custom format options (used when format='custom') */
    formatOptions?: DateTimeFormatOptions;
    /** Custom CSS classes */
    className?: string;
    /** Show as tooltip on hover */
    showTooltip?: boolean;
    /** Custom tooltip text */
    tooltip?: string;
}

/**
 * Timezone-aware DateTime component
 * 
 * Automatically converts UTC dates from database to user's local timezone
 * and formats them according to the user's language preference.
 * 
 * @example
 * // Date only
 * <DateTime date="2024-01-15T14:30:00Z" format="date" />
 * // Output: "Jan 15, 2024"
 * 
 * // Time only
 * <DateTime date="2024-01-15T14:30:00Z" format="time" />
 * // Output: "9:30 AM"
 * 
 * // Date and time
 * <DateTime date="2024-01-15T14:30:00Z" format="datetime" />
 * // Output: "Jan 15, 2024, 9:30 AM"
 * 
 * // Relative time
 * <DateTime date="2024-01-15T14:30:00Z" format="relative" />
 * // Output: "2 hours ago"
 * 
 * // Custom format with timezone
 * <DateTime 
 *   date="2024-01-15T14:30:00Z" 
 *   format="custom" 
 *   formatOptions={{ includeTimezone: true, includeSeconds: true }} 
 * />
 * // Output: "Jan 15, 2024, 9:30:00 AM EST"
 */
export function DateTime({
    date,
    format = 'datetime',
    formatOptions = {},
    className,
    showTooltip = true,
    tooltip,
}: DateTimeProps) {
    const {
        formatDate,
        formatTime,
        formatDateTime,
        formatWithTimezone,
        getRelativeTime,
        getTimezoneInfo
    } = useDateTimeFormat();

    const { timezone } = getTimezoneInfo();

    const renderDateTime = () => {
        switch (format) {
            case 'date':
                return formatDate(date, formatOptions);

            case 'time':
                return formatTime(date, formatOptions);

            case 'relative':
                return getRelativeTime(date);

            case 'custom':
                return formatDateTime(date, formatOptions);

            case 'datetime':
            default:
                return formatDateTime(date, formatOptions);
        }
    };

    const getTooltipText = () => {
        if (tooltip) return tooltip;

        if (format === 'relative') {
            return `${formatWithTimezone(date)} (${timezone})`;
        }

        return `${formatWithTimezone(date)} (${timezone})`;
    };

    return (
        <time
            dateTime={date}
            className={cn('whitespace-nowrap', className)}
            title={showTooltip ? getTooltipText() : undefined}
        >
            {renderDateTime()}
        </time>
    );
}

/**
 * Shortcut components for common use cases
 */
export function DateDisplay({
    date,
    className,
    showTooltip = true
}: {
    date: string;
    className?: string;
    showTooltip?: boolean;
}) {
    return <DateTime date={date} format="date" className={className} showTooltip={showTooltip} />;
}

export function TimeDisplay({
    date,
    includeSeconds = false,
    className,
    showTooltip = true
}: {
    date: string;
    includeSeconds?: boolean;
    className?: string;
    showTooltip?: boolean;
}) {
    return (
        <DateTime
            date={date}
            format="time"
            formatOptions={{ includeSeconds }}
            className={className}
            showTooltip={showTooltip}
        />
    );
}

export function RelativeTime({
    date,
    className,
    showTooltip = true
}: {
    date: string;
    className?: string;
    showTooltip?: boolean;
}) {
    return <DateTime date={date} format="relative" className={className} showTooltip={showTooltip} />;
}

export function DateTimeWithTimezone({
    date,
    className,
    showTooltip = true
}: {
    date: string;
    className?: string;
    showTooltip?: boolean;
}) {
    return (
        <DateTime
            date={date}
            format="custom"
            formatOptions={{ includeTimezone: true }}
            className={className}
            showTooltip={showTooltip}
        />
    );
}